import { DateTime } from 'luxon';

import { ReportDefinition } from '@/common/models';
import QueryParams from '@/common/models/query-params/QueryParams';
import ErrorHandler from '@/ErrorHandler';
import GlobalEventBus from '@/GlobalEventBus';
import GlobalEvents from '@/GlobalEvents';
import API from '@/org-app/api';
import {
  createAuditAction,
  createDeleteAction,
  createGetAllAction,
  createGetOneAction,
  createPostAction,
  createPutAction,
} from '@/utils/ActionsUtils';
import i18n from '@/vueI18n';

import * as types from './mutation-types';

const actions = {
  createNewReportDefinition(
    { commit },
    reportDefinition = new ReportDefinition(-1, '', {
      breakdown: 'CUMULATIVE',
      format: 'csv',
      metrics: [],
      dimensions: [],
      dates: {
        mode: 'RELATIVE',
        date_range: 'PREVIOUS_30_DAYS',
      },
      defaultReportOutputFormat: 'csv',
      timezone: DateTime.local().zone.name,
    }),
  ) {
    commit(types.CREATE_REPORTDEFINITION, reportDefinition);
    return reportDefinition;
  },
  getReportDefinitions: createGetAllAction(
    API.instance.getReportDefinitions,
    types.UPDATE_REPORTDEFINITIONS_LIST,
    'messages.failedToGetReportDefinitions',
    true,
  ),
  getReportDefinition: createGetOneAction(
    API.instance.getReportDefinition,
    types.UPDATE_REPORTDEFINITION,
    'messages.failedToGetReportDefinition',
  ),
  createReportDefinitions: createPostAction(
    API.instance.postReportDefinitions,
    types.SAVE_REPORTDEFINITIONS,
    'messages.failedToCreateReportDefinition',
  ),
  updateReportDefinition: createPutAction(
    API.instance.putReportDefinition,
    types.SAVE_REPORTDEFINITIONS,
    'messages.failedToUpdateReportDefinition',
  ),
  deleteReportDefinition: createDeleteAction(
    API.instance.deleteReportDefinition,
    types.DELETE_REPORTDEFINITION,
    'messages.failedToDeleteReportDefinition',
  ),
  getReportDefinitionAudit: createAuditAction(
    API.instance.getReportDefinitionAudit,
    'messages.failedToGetReportDefinitionAudit',
  ),

  async getReportSchedules(
    { commit },
    { queryParams = QueryParams.all(), cancelTokenSource = null, showLoader = false } = {},
  ) {
    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loading);
    }
    const reportSchedules = await API.instance.getReportSchedules({ queryParams, cancelTokenSource }).catch((err) => {
      const msg = i18n.global.t('messages.failedToGetReportSchedules');
      ErrorHandler.showError(msg, err);
      if (showLoader) {
        GlobalEventBus.$emit(GlobalEvents.loaded);
      }
      throw err;
    });

    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loaded);
    }
    return reportSchedules;
  },

  async runReportDefinition(_, { id, skipEmail = false, cancelTokenSource = null, showLoader = false }) {
    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loading);
    }

    const reportExecution = await API.instance
      .runReportDefinition({
        id,
        params: { skip_email: skipEmail },
        cancelTokenSource,
      })
      .catch((err) => {
        const msg = i18n.global.t('messages.failedToRunReportDefinition');
        ErrorHandler.showError(msg, err);
        if (showLoader) {
          GlobalEventBus.$emit(GlobalEvents.loaded);
        }
        throw err;
      });
    if (showLoader) {
      GlobalEventBus.$emit(GlobalEvents.loaded);
    }
    return reportExecution;
  },

  getReachAndFrequencyReports(_, { entityType, entityId, params }) {
    return new Promise((resolve, reject) => {
      API.instance.getReachAndFrequencyReports(
        entityType,
        entityId,
        params,
        (result) => {
          resolve(result);
        },
        (error, status) => {
          const msg = i18n.global.t('messages.failedToGetReachAndFrequencyReports');
          ErrorHandler.showError(msg, error, status);
          reject(error);
        },
      );
    });
  },
};

export default actions;
